import {
  Component,
  effect,
  inject,
  Injector,
  OnInit,
  signal,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { nanoid } from 'nanoid';
import { getFieldTypeIcons, resolveFieldTypeAlias } from '../../core/config/field-types.config';
import { AssetInstance } from '../../core/models/asset-instance.model';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { CreateFormParams, UpdateFormParams } from '../../core/models/form.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { EntitlementsService } from '../../core/services/entitlements.service';
import { FormDefinitionService } from '../../core/services/form-definition.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalConfig, ModalService } from '../../core/services/modal.service';
import { PermissionService } from '../../core/services/permission.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../shared/components/modal-dialog/modal-dialog.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TabStripComponent } from '../../shared/components/tab-strip/tab-strip.component';
import { TabComponent } from '../../shared/components/tab-strip/tab.component';
import { UpsellDialogComponent } from '../../shared/components/upsell-dialog/upsell-dialog.component';
import { TippyDirective } from '../../shared/directives/tippy.directive';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { FormBuilderComponent } from '../form-builder/form-builder.component';
import { getFormFormFields } from './form-form.definition';

@Component({
  selector: 'app-interaction-type-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    FormlyModule,
    FormBuilderComponent,
    SpinnerComponent,
    ButtonComponent,
    IconComponent,
    TabStripComponent,
    TabComponent,
    TippyDirective,
    TranslatePipe,
    ModalDialogComponent,
  ],
  templateUrl: './form-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-form.component.css',
})
export class FormFormComponent implements OnInit {
  private static readonly SAVE_ACTION_SAVE = 'save' as const;
  private static readonly SAVE_ACTION_SAVE_AND_CLOSE = 'saveAndClose' as const;
  private static readonly SAVE_FEEDBACK_MIN_MS = 250;
  private static readonly SAVE_SUCCESS_FEEDBACK_MS = 1200;

  // ...existing code...
  // Ensure customFormFields is updated when fieldsChanged is emitted from form builder
  onCustomFormFieldsChanged(fields: FormlyFieldConfig[]) {
    this.logger.debug(
      '[FormForm.onCustomFormFieldsChanged] 🔔 ===== FIELDS CHANGED EVENT RECEIVED =====',
    );
    this.logger.debug('[FormForm.onCustomFormFieldsChanged] Field count:', fields.length);
    this.logger.debug('[FormForm.onCustomFormFieldsChanged] Fields:', fields);
    this.logger.debug('[FormForm.onCustomFormFieldsChanged] ⚠️  Updating in-memory signal only');
    this.logger.debug('[FormForm.onCustomFormFieldsChanged] ⚠️  DATA NOT SAVED TO DATABASE YET!');
    this.logger.debug(
      '[FormForm.onCustomFormFieldsChanged] ⚠️  User must click "Save Form" button to persist changes',
    );
    this.customFormFields.set(fields);
    this.logger.debug('[FormForm.onCustomFormFieldsChanged] ✅ In-memory signal updated');
  }

  onColumnsChanged(cols: 1 | 2 | 3 | 4) {
    this.formColumns.set(cols);
  }
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breadcrumbService = inject(BreadcrumbService);
  private formService = inject(FormService);
  private checkPointService = inject(CheckPointService);
  private assetInstanceService = inject(AssetInstanceService);
  private injector = inject(Injector);
  private formDefinitionService = inject(FormDefinitionService);
  private permissionService = inject(PermissionService);
  private entitlementsService = inject(EntitlementsService);
  private modalService = inject(ModalService);
  private translate = inject(TranslationService);

  form = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];

  // Custom form fields defined in form builder
  customFormFields = signal<FormlyFieldConfig[]>([]);
  formDefinitionId = signal<string | null>(null);
  /** Column count for the rendered interaction form (1 = default single column). */
  formColumns = signal<1 | 2 | 3 | 4>(1);

  // Assets (checkpoints) associated with this interaction type
  checkPoints: Signal<CheckPoint[]> = signal([]);
  checkPointsLoading: Signal<boolean> = signal(false);
  checkPointsError: Signal<string | null> = signal(null);

  // Asset instances for each checkpoint, keyed by checkpoint id
  checkPointInstances: Signal<Map<string, AssetInstance[]>> = signal(new Map());

  loading = signal(true);
  saving = signal(false);
  activeSaveAction = signal<'save' | 'saveAndClose' | null>(null);
  saveButtonFeedback = signal<'idle' | 'saving' | 'saved'>('idle');
  error = signal<string | null>(null);
  isEditMode = signal(false);
  formId = signal<string | null>(null);
  canEdit = signal(false);
  canDelete = signal(false);
  showDeleteModal = signal(false);
  deleting = signal(false);

  // Active tab
  activeTab = signal<string>('details');

  // Return URL for navigation after save/cancel
  private returnUrl: string | null = null;

  private logger = inject(LoggerService).getLogger('interaction-type-form');
  private saveFeedbackToken = 0;

  constructor() {
    // Update URL when active tab changes
    effect(() => {
      const tab = this.activeTab();
      const typeId = this.formId();

      this.logger.debug('[FormForm.tabEffect] Tab changed to:', tab, 'for typeId:', typeId);
      this.logger.debug('[FormForm.tabEffect] Current URL:', this.router.url);

      if (typeId) {
        // Only navigate if the URL doesn't already match the target
        const targetUrl = `/forms/${typeId}/${tab}`;
        this.logger.debug('[FormForm.tabEffect] Target URL:', targetUrl);
        if (this.router.url !== targetUrl) {
          this.logger.debug('[FormForm.tabEffect] 🔄 Navigating to target URL');
          // Always include tab in URL for consistency, preserve query params
          this.router.navigate(['/forms', typeId, tab], {
            replaceUrl: true,
            queryParamsHandling: 'preserve',
          });
        } else {
          this.logger.debug(
            '[FormForm.tabEffect] URL already matches target, no navigation needed',
          );
        }
      }
    });
  }

  private fieldTypeIcons: Record<string, string> = getFieldTypeIcons();

  getFieldIcon(fieldType: string | undefined): string {
    if (!fieldType || typeof fieldType !== 'string') {
      return 'help-circle';
    }
    // TEMPORARY BACKWARD COMPATIBILITY: Resolve any field type aliases to canonical names
    const resolvedType = resolveFieldTypeAlias(fieldType);
    return this.fieldTypeIcons[resolvedType] || 'help-circle';
  }

  getFieldType(field: FormlyFieldConfig): string {
    // TEMPORARY BACKWARD COMPATIBILITY: Resolve any field type aliases to canonical names
    // This ensures old data using deprecated field type names (e.g., 'input') is handled correctly
    const rawType = typeof field.type === 'string' ? field.type : 'text';
    return resolveFieldTypeAlias(rawType);
  }

  async ngOnInit() {
    // Check permissions
    const id = this.route.snapshot.paramMap.get('id');
    const isNew = !id || id === 'new';
    const requiredPermission = isNew ? 'form.create' : 'form.update';
    this.canEdit.set(this.permissionService.has(requiredPermission));
    this.canDelete.set(this.permissionService.has('form.delete'));

    // Restore active tab from route if present, default to 'details'
    const tab = this.route.snapshot.paramMap.get('tab');
    if (tab && ['details', 'assets'].includes(tab)) {
      this.activeTab.set(tab);
    } else {
      this.activeTab.set('details');
    }

    // Capture returnUrl parameter for navigation after save/cancel
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    await this.loadFormDefinition();
    await this.loadForm();

    // Load associated assets if in edit mode
    if (this.isEditMode() && this.formId()) {
      this.loadCheckPoints();
    }
  }

  loadCheckPoints() {
    const typeId = this.formId();
    if (!typeId) return;

    const query = this.checkPointService.createQueryByFormId(typeId);
    this.checkPoints = query.data;
    this.checkPointsLoading = query.loading;
    this.checkPointsError = query.error;

    // Keep instances in sync with whichever checkpoints are currently loaded
    effect(
      () => {
        const ids = query
          .data()
          .map((checkPoint) => checkPoint.id)
          .filter((id): id is string => !!id);

        if (ids.length === 0) {
          this.checkPointInstances = signal(new Map());
          return;
        }

        const instancesQuery = this.assetInstanceService.createQueryByCheckpointIds(ids);
        this.checkPointInstances = instancesQuery.data;
      },
      { injector: this.injector },
    );
  }

  async loadFormDefinition() {
    try {
      let fields = getFormFormFields((key) => this.translate.translate(key));

      // Disable all fields if user can't edit
      if (!this.canEdit()) {
        fields = fields.map((field) => ({
          ...field,
          props: { ...field.props, disabled: true },
        }));
      }

      this.fields = fields;
    } catch (err) {
      this.logger.error('Failed to build form definition:', err);
      this.error.set('Failed to load form. Please try again.');
    }
  }

  async loadForm() {
    const id = this.route.snapshot.paramMap.get('id');

    if (id && id !== 'new') {
      this.isEditMode.set(true);
      this.formId.set(id);

      try {
        const form = await this.formService.getById(id);

        if (form) {
          this.breadcrumbService.setDynamicLabel(form.name);
          this.model = {
            name: form.name,
            prefill_last_values: form.prefill_last_values || false,
            auto_submit: form.auto_submit || false,
            play_success_sound: form.play_success_sound || false,
          };

          // Load custom form definition if it exists
          if (form.form_definition_id) {
            this.formDefinitionId.set(form.form_definition_id);
            const customFormDef = await this.formDefinitionService.getFormDefinitionFull(
              form.form_definition_id,
            );
            if (customFormDef) {
              this.customFormFields.set(customFormDef.fields);
              this.formColumns.set(customFormDef.columns);
            }
          }
        } else {
          this.error.set('Interaction type not found.');
        }
      } catch (err) {
        this.logger.error('Failed to load interaction type:', err);
        this.error.set('Failed to load interaction type. Please try again.');
      }
    } else {
      // New interaction type - set default values
      this.breadcrumbService.setDynamicLabel('New Form');
      this.model = {
        prefill_last_values: false,
        auto_submit: false,
        play_success_sound: false,
      };
    }

    this.loading.set(false);
  }

  async onSubmit() {
    await this.onSaveAndClose();
  }

  onCancel() {
    // Navigate to returnUrl if provided, otherwise default to forms list
    const destination = this.returnUrl || '/forms';
    this.router.navigate([destination]);
  }

  /**
   * Defense-in-depth re-check of the plan's custom-form hard cap, for direct
   * navigation to /forms/new that bypasses the list page's own gate (see
   * FormsComponent.createForm()). Editing an existing form is never gated --
   * only creating a new one.
   */
  private async checkFormLimit(): Promise<boolean> {
    let entitlements: Awaited<ReturnType<typeof this.entitlementsService.getCurrent>>;
    try {
      entitlements = await this.entitlementsService.getCurrent();
    } catch (err) {
      this.logger.warn('Failed to load entitlements, allowing form creation:', err);
      return true; // fail open if entitlements haven't synced/loaded yet
    }
    if (!entitlements) {
      return true; // fail open if entitlements haven't synced yet
    }

    const currentCount = await this.formService.count();
    if (!this.entitlementsService.limitReached(currentCount, entitlements.custom_form_limit)) {
      return true;
    }

    const closeSignal = signal<{ action: string } | null>(null);
    const config: ModalConfig = {
      component: UpsellDialogComponent,
      componentInputs: { limitType: 'customForm', planName: entitlements.plan_name },
      title: this.translate.translate('upsellDialog.title'),
      size: 'small',
      closeSignal,
      actionButtons: [
        {
          id: 'cancel',
          label: this.translate.translate('upsellDialog.cancel'),
          variant: 'text',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'upgrade',
          label: this.translate.translate('upsellDialog.upgradePlan'),
          variant: 'primary',
          onClick: async () => closeSignal.set({ action: 'upgrade' }),
        },
      ],
    };

    const result = await this.modalService.openModal(config);
    if (result?.action === 'upgrade') {
      this.router.navigate(['/settings/billing']);
    }
    return false;
  }

  /**
   * Core save logic shared by onSave() and onSaveAndClose().
   * Returns the saved/created interaction type ID.
   */
  private async doSave(): Promise<string> {
    const formDefId = await this.saveCustomFormDefinition();

    if (this.isEditMode() && this.formId()) {
      const params: UpdateFormParams = {
        name: this.model.name,
        form_definition_id: formDefId || undefined,
        prefill_last_values: this.model.prefill_last_values || false,
        auto_submit: this.model.auto_submit || false,
        play_success_sound: this.model.play_success_sound || false,
      };
      await this.formService.update(this.formId()!, params);
      return this.formId()!;
    } else {
      const params: CreateFormParams = {
        name: this.model.name,
        form_definition_id: formDefId || undefined,
        prefill_last_values: this.model.prefill_last_values || false,
        auto_submit: this.model.auto_submit || false,
        play_success_sound: this.model.play_success_sound || false,
      };
      const created = await this.formService.create(params);
      return created.id!;
    }
  }

  /**
   * Save the form and remain on the page.
   * If this is a new interaction type, transitions the URL to the edit URL.
   */
  async onSave() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const feedbackToken = ++this.saveFeedbackToken;
    const saveStartedAt = Date.now();
    this.activeSaveAction.set(FormFormComponent.SAVE_ACTION_SAVE);
    this.saveButtonFeedback.set('saving');
    this.saving.set(true);
    this.error.set(null);

    if (!this.isEditMode() && !(await this.checkFormLimit())) {
      this.activeSaveAction.set(null);
      this.saveButtonFeedback.set('idle');
      this.saving.set(false);
      return;
    }

    try {
      const wasNew = !this.isEditMode();
      const savedId = await this.doSave();

      const elapsed = Date.now() - saveStartedAt;
      const minimumFeedbackRemaining = FormFormComponent.SAVE_FEEDBACK_MIN_MS - elapsed;
      if (minimumFeedbackRemaining > 0) {
        await this.delay(minimumFeedbackRemaining);
      }

      if (wasNew) {
        this.formId.set(savedId);
        this.isEditMode.set(true);
        this.router.navigate(['/forms', savedId], { replaceUrl: true });
      }

      if (feedbackToken === this.saveFeedbackToken) {
        this.saveButtonFeedback.set('saved');
      }
    } catch (err) {
      this.logger.error('Failed to save interaction type:', err);
      this.error.set('Failed to save interaction type. Please try again.');
      if (feedbackToken === this.saveFeedbackToken) {
        this.saveButtonFeedback.set('idle');
      }
    } finally {
      this.activeSaveAction.set(null);
      this.saving.set(false);
    }

    if (feedbackToken === this.saveFeedbackToken && this.saveButtonFeedback() === 'saved') {
      await this.delay(FormFormComponent.SAVE_SUCCESS_FEEDBACK_MS);
      if (feedbackToken === this.saveFeedbackToken) {
        this.saveButtonFeedback.set('idle');
      }
    }
  }

  /**
   * Save the form and navigate away (original save behaviour).
   */
  async onSaveAndClose() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saveFeedbackToken += 1;
    this.saveButtonFeedback.set('idle');
    this.activeSaveAction.set(FormFormComponent.SAVE_ACTION_SAVE_AND_CLOSE);
    this.saving.set(true);
    this.error.set(null);

    if (!this.isEditMode() && !(await this.checkFormLimit())) {
      this.activeSaveAction.set(null);
      this.saving.set(false);
      return;
    }

    try {
      await this.doSave();

      const destination = this.returnUrl || '/forms';
      this.router.navigate([destination]);
    } catch (err) {
      this.logger.error('Failed to save interaction type:', err);
      this.error.set('Failed to save interaction type. Please try again.');
    } finally {
      this.activeSaveAction.set(null);
      this.saving.set(false);
    }
  }

  submitForm() {
    // Trigger form submission programmatically — delegates to onSaveAndClose()
    this.onSaveAndClose();
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  private async saveCustomFormDefinition(): Promise<string | null> {
    const fields = this.customFormFields();
    this.logger.debug('[saveCustomFormDefinition] 📝 ===== SAVING CUSTOM FORM DEFINITION =====');
    this.logger.debug('[saveCustomFormDefinition] Field count:', fields.length);
    this.logger.debug('[saveCustomFormDefinition] Field data:', fields);

    if (fields.length === 0) {
      this.logger.debug('[saveCustomFormDefinition] ℹ️  No custom form fields - nothing to save');
      return null; // No custom form fields
    }

    // Generate or reuse form definition ID
    const formDefId = this.formDefinitionId() || nanoid();
    const formName = `${this.model.name} Form`;

    this.logger.debug('[saveCustomFormDefinition] Form definition ID:', formDefId);
    this.logger.debug('[saveCustomFormDefinition] Form name:', formName);

    try {
      this.logger.debug(
        '[saveCustomFormDefinition] 📝 Calling formDefinitionService.saveFormDefinition()...',
      );
      await this.formDefinitionService.saveFormDefinition(
        formDefId,
        fields,
        formName,
        this.formColumns(),
      );
      this.logger.debug('[saveCustomFormDefinition] ✅ Form definition saved successfully!');
      this.logger.debug('Saved form definition:', formDefId);
      return formDefId;
    } catch (error) {
      this.logger.error('[saveCustomFormDefinition] ❌ Failed to save form definition:', error);
      throw error;
    }
  }

  /**
   * Show QR code for a specific asset/checkpoint with this interaction type
   */
  showCheckPointQrCode(asset: CheckPoint) {
    const typeId = this.formId();
    if (!typeId || !asset.id) return;

    // Navigate to view page
    this.router.navigate(['/checkpoints', asset.id, typeId, 'view'], {
      queryParams: { returnUrl: `/forms/${typeId}/${this.activeTab()}` },
    });
  }

  /**
   * Navigate to the View screen for a specific asset/checkpoint with this interaction type
   */
  showFormView(asset: CheckPoint) {
    const typeId = this.formId();
    if (!typeId || !asset.id) return;

    this.router.navigate(['/checkpoints', asset.id, typeId, 'view'], {
      queryParams: { returnUrl: `/forms/${typeId}/${this.activeTab()}` },
    });
  }

  /**
   * Navigate to record interaction page for this asset and interaction type
   */
  recordInteraction(asset: CheckPoint) {
    const typeId = this.formId();
    if (!typeId || !asset.id) return;

    this.router.navigate(['/submission', asset.id, typeId], {
      queryParams: { returnUrl: `/forms/${typeId}/${this.activeTab()}` },
    });
  }

  /**
   * Instances of this checkpoint, if any. Checkpoints with instances are
   * recorded against a specific instance rather than the checkpoint itself.
   */
  getInstancesFor(checkPoint: CheckPoint): AssetInstance[] {
    if (!checkPoint.id) return [];
    return this.checkPointInstances().get(checkPoint.id) ?? [];
  }

  /**
   * Navigate to record interaction page for a specific instance of this asset
   */
  recordInteractionForInstance(asset: CheckPoint, instance: AssetInstance) {
    const typeId = this.formId();
    if (!typeId || !asset.id) return;

    this.router.navigate(['/submission', asset.id, typeId, instance.id], {
      queryParams: { returnUrl: `/forms/${typeId}/${this.activeTab()}` },
    });
  }

  /**
   * Navigate to interaction history page for this asset and interaction type
   */
  viewFormSubmission(asset: CheckPoint) {
    const typeId = this.formId();
    if (!typeId || !asset.id) return;

    this.router.navigate(['/submission', asset.id, typeId, 'history'], {
      queryParams: { returnUrl: `/forms/${asset.id}` },
    });
  }

  openDeleteModal(): void {
    this.showDeleteModal.set(true);
  }

  cancelDelete(): void {
    this.showDeleteModal.set(false);
  }

  async confirmDelete(): Promise<void> {
    const id = this.formId();
    if (!id) return;

    this.deleting.set(true);
    try {
      this.showDeleteModal.set(false);
      await this.formService.delete(id);
      this.router.navigate(['/forms']);
    } catch (err) {
      this.logger.error('Failed to delete interaction type:', err);
      this.error.set('Failed to delete. Please try again.');
    } finally {
      this.deleting.set(false);
    }
  }
}
